/*
 * Follow-ups and the audit search.
 *
 * A follow-up is closed by saying what was done, not by ticking it — so the next person reads
 * "Rang the surgery, Dr Shah will review on Monday" rather than wondering.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { Overlay } from './overlay.tsx';

type Task = {
  id: string;
  residentName: string;
  kind: string;
  title: string;
  detail: string;
  urgency: 'now' | 'today' | 'soon';
  createdAt: string;
  createdBy: string;
  doneAt: string | null;
  doneBy: string;
  outcome: string;
};

type Entry = {
  id: string;
  at: string;
  who: string;
  what: string;
  about: string | null;
  detail: Record<string, unknown>;
};

const URGENCY: Record<Task['urgency'], string> = {
  now: 'Now',
  today: 'Today',
  soon: 'Soon',
};

export function TasksScreen({ homeId }: { homeId: string }) {
  const [tab, setTab] = useState<'open' | 'done' | 'audit'>('open');
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [closing, setClosing] = useState<Task | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (tab === 'audit') return;
    try {
      const data = await api.get<{ tasks: Task[] }>(`/api/homes/${homeId}/tasks?status=${tab}`);
      setTasks(data.tasks);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the follow-ups.');
    }
  }, [homeId, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <div className="head">
        <h1>Follow-ups</h1>
        <div className="row-actions">
          <button type="button" onClick={() => setAdding(true)}>Add one</button>
          <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      <p className="muted">
        Raised by the system when something needs doing about it, and closed by saying what was done.
      </p>

      <nav className="tabs">
        <button type="button" className={tab === 'open' ? 'on' : ''} onClick={() => setTab('open')}>Still to do</button>
        <button type="button" className={tab === 'done' ? 'on' : ''} onClick={() => setTab('done')}>Done</button>
        <button type="button" className={tab === 'audit' ? 'on' : ''} onClick={() => setTab('audit')}>Audit trail</button>
      </nav>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {tab === 'audit' ? <AuditSearch homeId={homeId} /> : null}

      {tab !== 'audit' ? (
        <>
          {tasks && !tasks.length ? (
            <p className="notice">{tab === 'open' ? 'Nothing to follow up.' : 'Nothing closed yet.'}</p>
          ) : null}

          <ul className="list">
            {(tasks ?? []).map((task) => (
              <li key={task.id}>
                <div className={`row static column dose ${task.urgency === 'now' && !task.doneAt ? 'overdue' : ''}`}>
                  <div className="row-line">
                    <span className="row-main">{task.title}</span>
                    {!task.doneAt ? (
                      <span className={`chip ${task.urgency === 'now' ? 'warn' : ''}`}>{URGENCY[task.urgency]}</span>
                    ) : (
                      <span className="chip done">Done</span>
                    )}
                  </div>
                  {task.detail ? <p className="muted small">{task.detail}</p> : null}
                  <p className="muted small">
                    {task.residentName ? `${task.residentName} · ` : ''}
                    raised {formatWhen(task.createdAt)} by {task.createdBy}
                  </p>

                  {task.doneAt ? (
                    <p className="muted small">
                      <strong>{task.doneBy}</strong> at {formatWhen(task.doneAt)}: {task.outcome}
                    </p>
                  ) : (
                    <div className="row-actions">
                      <button type="button" onClick={() => setClosing(task)}>Say what was done</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {closing ? (
        <CloseDialog
          task={closing}
          onClose={() => setClosing(null)}
          onDone={() => {
            setClosing(null);
            void load();
          }}
        />
      ) : null}

      {adding ? (
        <AddDialog
          homeId={homeId}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            setTab('open');
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

function CloseDialog({ task, onClose, onDone }: { task: Task; onClose: () => void; onDone: () => void }) {
  const [outcome, setOutcome] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/tasks/${task.id}/done`, { outcome });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [task.id, outcome, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{task.title}</h2>
        {task.detail ? <p className="muted">{task.detail}</p> : null}

        <label htmlFor="outcome">What did you do about it?</label>
        <input id="outcome" required minLength={3} value={outcome} onChange={(e) => setOutcome(e.target.value)}
          placeholder="Rang the surgery, Dr Shah will review it on Monday" />
        <p className="muted small">This stays on the record, so write what the next person needs to know.</p>

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Close this follow-up'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function AddDialog({ homeId, onClose, onDone }: { homeId: string; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [urgency, setUrgency] = useState<'now' | 'today' | 'soon'>('today');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/homes/${homeId}/tasks`, { title, detail, urgency });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [homeId, title, detail, urgency, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Something that needs doing</h2>

        <label htmlFor="task-title">What is it?</label>
        <input id="task-title" required minLength={3} value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Ask the GP to review Mrs Ellison's painkillers" />

        <label htmlFor="task-detail">Anything else the person doing it needs</label>
        <input id="task-detail" value={detail} onChange={(e) => setDetail(e.target.value)} />

        <label htmlFor="task-urgency">When</label>
        <select id="task-urgency" value={urgency} onChange={(e) => setUrgency(e.target.value as 'now' | 'today' | 'soon')}>
          <option value="now">Now</option>
          <option value="today">Today</option>
          <option value="soon">Soon</option>
        </select>

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add it'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function AuditSearch({ homeId }: { homeId: string }) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState('');

  const search = useCallback(async () => {
    try {
      const query = new URLSearchParams();
      if (q) query.set('q', q);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const data = await api.get<{ entries: Entry[] }>(`/api/homes/${homeId}/audit?${query.toString()}`);
      setEntries(data.entries);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not search the audit trail.');
    }
  }, [homeId, q, from, to]);

  useEffect(() => {
    void search();
    // Only on first look; after that it is searched on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <p className="muted">
        Everything the system has recorded. Nothing here can be changed or deleted, by anyone.
      </p>

      <div className="slot-top">
        <div>
          <label htmlFor="audit-q">Search</label>
          <input id="audit-q" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="a name, a medicine, or what happened" />
        </div>
        <div className="narrow">
          <label htmlFor="audit-from">From</label>
          <input id="audit-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="narrow">
          <label htmlFor="audit-to">To</label>
          <input id="audit-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" onClick={() => void search()}>Search</button>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {entries && !entries.length ? <p className="muted">Nothing matches that.</p> : null}

      <div className="scroll">
        <table className="register">
          <thead><tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr></thead>
          <tbody>
            {(entries ?? []).map((entry) => (
              <tr key={entry.id}>
                <td>{formatWhen(entry.at)}</td>
                <td>{entry.who}</td>
                <td>{entry.what}</td>
                <td><small>{describe(entry.detail)}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function describe(detail: Record<string, unknown>): string {
  return Object.entries(detail ?? {})
    .filter(([, value]) => value !== null && value !== '' && value !== undefined)
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(' · ');
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)} ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
